import { addDays } from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { parseISO } from 'date-fns';
import type { FormField } from '@/types/form-definition';

/** Default when `scheduleTimeZone` is missing from field config or API payload (legacy JSON). */
export const DEFAULT_SCHEDULE_TIME_ZONE = 'America/New_York';

/**
 * Literal suffix for US Eastern business timestamps in admin UI.
 * Wall clock uses IANA `America/New_York` (DST-aware); label is school policy “EST”.
 */
export const US_EASTERN_DISPLAY_SUFFIX = ' EST';

/**
 * First non-empty IANA string wins; otherwise {@link DEFAULT_SCHEDULE_TIME_ZONE}.
 * Pass candidates in priority order (e.g. slot response, then field config).
 */
export function resolveScheduleTimeZone(...candidates: (string | undefined | null)[]): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return DEFAULT_SCHEDULE_TIME_ZONE;
}

export function scheduleTimeZoneFromField(field: FormField | undefined): string {
  if (!field?.config || typeof field.config !== 'object' || Array.isArray(field.config)) {
    return DEFAULT_SCHEDULE_TIME_ZONE;
  }
  const cfg = field.config as Record<string, unknown>;
  return resolveScheduleTimeZone(typeof cfg.scheduleTimeZone === 'string' ? cfg.scheduleTimeZone : undefined);
}

/** Literal suffix after formatted date/time (see docs/ADMIN-PORTAL-APPOINTMENT-TIMEZONE.md). */
export function scheduleZoneDisplaySuffix(iana: string): string {
  const z = resolveScheduleTimeZone(iana);
  const map: Record<string, string> = {
    'America/New_York': US_EASTERN_DISPLAY_SUFFIX,
    'America/Chicago': ' CT',
    'America/Denver': ' MT',
    'America/Los_Angeles': ' PT',
  };
  return map[z] ?? ` (${z})`;
}

/** Format a single instant (ISO 8601) in the schedule IANA zone + controlled zone suffix. */
export function formatIsoInScheduleZone(iso: string | undefined, timeZone: string): string {
  if (!iso) return '—';
  const tz = resolveScheduleTimeZone(timeZone);
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    const core = formatInTimeZone(d, tz, 'MMM d, yyyy h:mm a');
    return `${core}${scheduleZoneDisplaySuffix(tz)}`;
  } catch {
    return iso;
  }
}

/**
 * Format start/end instants in the schedule zone; compact same-calendar-day range in that zone.
 * One zone suffix at end of the line (not repeated on start/end).
 */
export function formatAppointmentRangeInZone(
  startIso: string | undefined,
  endIso: string | null | undefined,
  timeZone: string
): string {
  const tz = resolveScheduleTimeZone(timeZone);
  const sfx = scheduleZoneDisplaySuffix(tz);

  if (!startIso) return '—';
  const start = parseISO(startIso);
  if (Number.isNaN(start.getTime())) return startIso;

  if (!endIso) {
    try {
      const core = formatInTimeZone(start, tz, 'MMM d, yyyy h:mm a');
      return `${core}${sfx}`;
    } catch {
      return startIso;
    }
  }

  const end = parseISO(endIso);
  if (Number.isNaN(end.getTime())) {
    try {
      const core = formatInTimeZone(start, tz, 'MMM d, yyyy h:mm a');
      return `${core}${sfx} → ${endIso}`;
    } catch {
      return `${startIso} → ${endIso}`;
    }
  }

  try {
    const dayKey = (d: Date) => formatInTimeZone(d, tz, 'yyyy-MM-dd');
    if (dayKey(start) === dayKey(end)) {
      const dayPart = formatInTimeZone(start, tz, 'EEE, MMM d');
      const t1 = formatInTimeZone(start, tz, 'h:mm a');
      const t2 = formatInTimeZone(end, tz, 'h:mm a');
      return `${dayPart}, ${t1} – ${t2}${sfx}`;
    }
    const coreStart = formatInTimeZone(start, tz, 'MMM d, yyyy h:mm a');
    const coreEnd = formatInTimeZone(end, tz, 'MMM d, yyyy h:mm a');
    return `${coreStart} → ${coreEnd}${sfx}`;
  } catch {
    try {
      const cs = formatInTimeZone(start, tz, 'MMM d, yyyy h:mm a');
      const ce = formatInTimeZone(end, tz, 'MMM d, yyyy h:mm a');
      return `${cs} → ${ce}${sfx}`;
    } catch {
      return startIso;
    }
  }
}

/**
 * Weekly schedule uses wall-clock minutes from midnight on `ymd` (YYYY-MM-DD) in `timeZone`.
 */
export function formatWallMinutesOnDate(ymd: string, minutesFromMidnight: number, timeZone: string): string {
  const h = Math.floor(minutesFromMidnight / 60);
  const min = minutesFromMidnight % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const naive = `${ymd}T${pad(h)}:${pad(min)}:00`;
  const tz = resolveScheduleTimeZone(timeZone);
  try {
    const d = toDate(naive, { timeZone: tz });
    if (Number.isNaN(d.getTime())) return `${pad(h)}:${pad(min)}`;
    const core = formatInTimeZone(d, tz, 'h:mm a');
    return `${core}${scheduleZoneDisplaySuffix(tz)}`;
  } catch {
    return `${pad(h)}:${pad(min)}`;
  }
}

/** Calendar heading for a picked calendar day, labeled consistently in the schedule zone (noon anchor avoids DST edge noise). */
export function formatScheduleDayHeading(ymd: string, timeZone: string, pattern: string): string {
  try {
    const d = toDate(`${ymd}T12:00:00`, { timeZone });
    if (Number.isNaN(d.getTime())) return ymd;
    return formatInTimeZone(d, timeZone, pattern);
  } catch {
    return ymd;
  }
}

/** Start of calendar day `ymd` (`YYYY-MM-DD`) in `timeZone`, as a UTC ISO instant. */
export function utcIsoStartOfZonedDay(ymd: string, timeZone: string): string {
  const tz = resolveScheduleTimeZone(timeZone);
  const d = toDate(`${ymd}T00:00:00`, { timeZone: tz });
  return d.toISOString();
}

/**
 * Next calendar date after `ymd` in the same IANA zone (noon anchor avoids most DST edge cases).
 */
export function nextYmdInZonedCalendar(ymd: string, timeZone: string): string {
  const tz = resolveScheduleTimeZone(timeZone);
  const anchor = toDate(`${ymd}T12:00:00`, { timeZone: tz });
  const next = addDays(anchor, 1);
  return formatInTimeZone(next, tz, 'yyyy-MM-dd');
}
